import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDataService } from '@/lib/service-context';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@erp/ui';
import { Button } from '@erp/ui';
import { SlidersHorizontal, GripVertical, Eye, EyeOff, RotateCcw } from 'lucide-react';

export interface ColDef {
  key: string;
  labelKey: string;
  locked?: boolean;
}

interface UseColumnConfigOptions {
  storageKey: string;
  allColumns: ColDef[];
  defaultColumns: string[];
}

export function useColumnConfig({ storageKey, allColumns, defaultColumns }: UseColumnConfigOptions) {
  const ds = useDataService();
  const loaded = useRef(false);
  const normalize = useCallback((cols: string[]): string[] => {
    const available = new Set(allColumns.map((c) => c.key));
    const locked = allColumns.filter((c) => c.locked).map((c) => c.key);
    return [...locked, ...cols.filter((key) => available.has(key) && !locked.includes(key))];
  }, [allColumns]);

  // Start with localStorage (instant), then overwrite from backend
  const loadLocal = (): string[] => {
    try { const r = localStorage.getItem(storageKey); if (r) return normalize(JSON.parse(r)); } catch {}
    return normalize(defaultColumns);
  };

  const [visibleCols, setVisibleCols] = useState<string[]>(loadLocal);

  useEffect(() => {
    setVisibleCols((prev) => normalize(prev));
  }, [normalize]);

  // Load from backend on mount (overwrites localStorage cache)
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    ds.userPreferences.get(storageKey).then((val) => {
      if (val && Array.isArray(val) && val.length > 0) {
        const normalized = normalize(val as string[]);
        setVisibleCols(normalized);
        localStorage.setItem(storageKey, JSON.stringify(normalized));
      }
    }).catch(() => { /* ignore — use localStorage fallback */ });
  }, [ds, storageKey, normalize]);

  const save = useCallback((cols: string[]) => {
    const normalized = normalize(cols);
    // Save to localStorage (instant)
    localStorage.setItem(storageKey, JSON.stringify(normalized));
    // Save to backend (async, fire-and-forget)
    ds.userPreferences.set(storageKey, normalized).catch(() => {});
  }, [ds, storageKey, normalize]);

  const toggleCol = useCallback((key: string) => {
    setVisibleCols((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      save(next);
      return next;
    });
  }, [save]);

  const moveCol = useCallback((from: number, to: number) => {
    setVisibleCols((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      save(next);
      return next;
    });
  }, [save]);

  const resetCols = useCallback(() => {
    const normalized = normalize(defaultColumns);
    setVisibleCols(normalized);
    save(normalized);
  }, [defaultColumns, normalize, save]);

  return { visibleCols, toggleCol, moveCol, resetCols, allColumns };
}

interface ColumnConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visibleCols: string[];
  allColumns: ColDef[];
  toggleCol: (key: string) => void;
  moveCol: (from: number, to: number) => void;
  resetCols: () => void;
}

export function ColumnConfigDialog({ open, onOpenChange, visibleCols, allColumns, toggleCol, moveCol, resetCols }: ColumnConfigDialogProps) {
  const { t } = useTranslation();
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const hiddenColumns = allColumns.filter((c) => !c.locked && !visibleCols.includes(c.key));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm max-h-[85vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('settings.columns.title')}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto pe-1">
          <div className="space-y-1">
            <p className="text-xs font-semibold text-muted-foreground">{t('settings.columns.visible')}</p>
            {visibleCols.map((key, idx) => {
              const col = allColumns.find((c) => c.key === key);
              if (!col) return null;
              const isDragging = dragIdx === idx;
              const isDropTarget = dragOverIdx === idx && dragIdx !== null && dragIdx !== idx;
              return (
                <div key={key} className="relative">
                  {isDropTarget && dragIdx !== null && dragIdx > idx && (
                    <div className="absolute -top-0.5 left-0 right-0 h-0.5 bg-primary rounded-full" />
                  )}
                  <div
                    draggable={!col.locked}
                    onDragStart={(e) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; }}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverIdx(idx); }}
                    onDragLeave={() => setDragOverIdx(null)}
                    onDrop={() => { if (dragIdx !== null && dragIdx !== idx) moveCol(dragIdx, idx); setDragIdx(null); setDragOverIdx(null); }}
                    onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                    className={`flex items-center justify-between rounded-md border px-2 py-1.5 text-sm transition-all ${isDragging ? 'opacity-30 scale-95' : ''} ${isDropTarget ? 'border-primary bg-primary/5' : ''} ${col.locked ? 'bg-muted/30' : 'cursor-grab active:cursor-grabbing hover:bg-accent/50'}`}
                  >
                    <div className="flex items-center gap-2">
                      {!col.locked ? <GripVertical size={14} className="text-muted-foreground/40" /> : <div className="w-3.5" />}
                      <span>{t(col.labelKey)}</span>
                    </div>
                    {!col.locked && (
                      <Button variant="ghost" size="icon-xs" onClick={() => toggleCol(key)} className="text-muted-foreground hover:text-foreground">
                        <EyeOff size={14} />
                      </Button>
                    )}
                  </div>
                  {isDropTarget && dragIdx !== null && dragIdx < idx && (
                    <div className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-primary rounded-full" />
                  )}
                </div>
              );
            })}
          </div>

          <div className="space-y-1">
            <p className="text-xs font-semibold text-muted-foreground">{t('settings.columns.hidden')}</p>
            {hiddenColumns.length > 0 ? (
              hiddenColumns.map((col) => (
                <div
                  key={col.key}
                  className="flex items-center justify-between rounded-md border border-dashed px-2 py-1.5 text-sm text-muted-foreground cursor-pointer hover:bg-accent"
                  onClick={() => toggleCol(col.key)}
                >
                  <span>{t(col.labelKey)}</span>
                  <Eye size={14} />
                </div>
              ))
            ) : (
              <div className="rounded-md border border-dashed px-2 py-1.5 text-sm text-muted-foreground">
                {t('settings.columns.allVisible')}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={resetCols}>
            <RotateCcw size={14} className="me-1.5" />{t('settings.columns.resetDefault')}
          </Button>
          <Button size="sm" onClick={() => onOpenChange(false)}>{t('common.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Button that opens the column config dialog. Icon-only on mobile,
 *  icon + label on sm+ screens. */
export function ColumnConfigButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  const label = t('settings.columns.title');
  return (
    <Button
      variant="outline"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="ms-auto size-9 sm:size-auto sm:h-9 sm:px-3 sm:gap-2 p-0"
    >
      <SlidersHorizontal size={14} />
      <span className="hidden sm:inline">{label}</span>
    </Button>
  );
}
